// The milestone cards waiting for the signed-in person (PLAN-2026-10-29-
// milestones-as-they-happen §2, scenario H7): a team they run reached a tier
// when its week settled, and the card shows the next time the site is open.
//
// THE QUEUE comes from `milestones` on the pulse the bell asks for (`meFeed`).
// A card is queued once per tab, taken when the screen is clear (the same test
// as the achievements' card, `momentsClear`, which also waits for that card to
// go), and marked seen as it shows, which takes it off the pulse for every
// device. A tab that closes before it shows leaves it unseen, so it shows next
// time.

import { useSyncExternalStore } from 'react';
import { MilestonesSeen, type MilestoneMoment } from '@fsp/domain';
import { send } from './api';
import { onMe } from './meFeed';

interface Queue {
  userId: string;
  /** Not shown yet, newest first. */
  pending: MilestoneMoment[];
}

let queue: Queue | null = null;
/** `${userId}|${id}`: shown in this tab, so a pulse that crosses the seen mark does not show it again. */
const shown = new Set<string>();
const listeners = new Set<() => void>();

function publish(next: Queue | null): void {
  queue = next;
  for (const l of listeners) l();
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

onMe((news) => {
  // AN ANSWER THAT DID NOT SAY IS NOT "NOTHING WAITS": `/api/me` carries no
  // milestones, and reading it as empty would drop a card waiting behind an
  // open sheet until the next pulse.
  if (news.milestones === undefined) return;
  const userId = news.userId;
  const unseen = news.milestones.filter((m) => !shown.has(`${userId}|${m.id}`));
  // WHAT ANOTHER DEVICE SHOWED LEAVES THE QUEUE: no longer in the answer, it
  // must not show here once the sheet in the way closes.
  const held = (queue?.userId === userId ? queue.pending : []).filter((h) =>
    unseen.some((m) => m.id === h.id),
  );
  const fresh = unseen.filter((m) => !held.some((h) => h.id === m.id));
  if (fresh.length === 0 && queue?.userId === userId && held.length === queue.pending.length)
    return;
  publish({ userId, pending: [...fresh, ...held] });
});

/** What is waiting to be shown to the signed-in person. */
export function usePendingMilestones(userId: string | null): MilestoneMoment[] {
  const q = useSyncExternalStore(subscribe, () => queue);
  return q !== null && q.userId === userId ? q.pending : NONE;
}
const NONE: MilestoneMoment[] = [];

/** Take everything waiting, for one card, and mark it shown here. */
export function takeMilestones(userId: string): MilestoneMoment[] {
  if (queue === null || queue.userId !== userId || queue.pending.length === 0) return [];
  const taken = queue.pending;
  for (const m of taken) shown.add(`${userId}|${m.id}`);
  publish({ userId, pending: [] });
  return taken;
}

/**
 * Say they were shown, on any device (`POST /api/me/milestones/seen`). A
 * failure is quiet: they are shown here already, and the next device shows
 * them once more, which is the honest outcome of a lost request.
 */
export async function markMilestonesSeen(ids: readonly string[]): Promise<void> {
  if (ids.length === 0) return;
  await send('POST', '/me/milestones/seen', { ids }, MilestonesSeen);
}
